/**
 * Аварийная схема площадки без движка карты (план 21.09, п.5). Рисует модель
 * из emergency/svg-scheme.ts: контуры приглушённо, маршрут — сигнальной
 * линией, «по прямой» — пунктиром, укрытия — капсулами с подписью.
 * Два места применения — окно героя, пока карты ещё нет, и весь экран, если
 * движок карты не загрузился вовсе (`engineFailed`).
 */
import type { LonLat } from '@app/geo'
import { getLang, tr } from '@app/i18n'
import type { Venue } from '@app/rvf'
import type { EscapeRoute } from '../emergency/escape-route'
import { buildSvgScheme, type SchemeShelter } from '../emergency/svg-scheme'
import { engineFailed, geoStart, langTick, venueMap, venue as venueSignal } from '../state'

/** Ширина капсулы укрытия по длине подписи (шрифт схемы моноширинный). */
function capsuleWidth(label: string): number {
  return Math.max(18, 10 + label.length * 5.4)
}

function ShelterMark({ s }: { s: SchemeShelter }) {
  const w = capsuleWidth(s.label)
  return (
    <g class={s.selected ? 'em-svg-shelter em-svg-shelter--on' : 'em-svg-shelter'}>
      <rect x={s.x - w / 2} y={s.y - 7} width={w} height={14} rx={7} />
      <text x={s.x} y={s.y + 3.6} text-anchor="middle">
        {s.label}
      </text>
    </g>
  )
}

export interface SchemeProps {
  readonly venue: Venue
  readonly route: EscapeRoute
  readonly shelterId: string | null
  readonly start: LonLat | null
}

/** Сама схема: чистый SVG, ничего не анимируется (анти-паника). */
export function EmergencySvgScheme({ venue, route, shelterId, start }: SchemeProps) {
  langTick.value
  const scheme = buildSvgScheme({ venue, route, shelterId, start, lang: getLang() })
  const { box } = scheme
  return (
    <svg
      class="em-svg"
      viewBox={`0 0 ${box.w} ${box.h}`}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label={tr('emergency.svg_scheme')}
    >
      {scheme.footprints.map((d) => (
        <path key={d} class="em-svg-unit" d={d} />
      ))}
      {scheme.direct && <path class="em-svg-direct" d={scheme.direct} />}
      {scheme.route && <path class="em-svg-route" d={scheme.route} />}
      {scheme.shelters.map((s) => (
        <ShelterMark key={s.id} s={s} />
      ))}
      {scheme.start && (
        <g class="em-svg-me">
          <circle cx={scheme.start.x} cy={scheme.start.y} r={5} />
          <text x={scheme.start.x} y={scheme.start.y - 10} text-anchor="middle">
            {tr('map.you_are_here')}
          </text>
        </g>
      )}
    </svg>
  )
}

/** Общий родитель обоих применений: тянет площадку и точку старта из сигналов. */
function SchemeFromState({ route, shelterId }: Omit<SchemeProps, 'venue' | 'start'>) {
  const v = venueSignal.value
  if (!v) return null
  return <EmergencySvgScheme venue={v} route={route} shelterId={shelterId} start={geoStart.value} />
}

/**
 * Окно героя: линии эвакуации раньше тайлов. Как только карта создана
 * (`venueMap`), схема уступает место живой карте — без перескока, окно то же.
 */
export function EmergencyHeroScheme(props: Omit<SchemeProps, 'venue' | 'start'>) {
  if (venueMap.value) return null
  return (
    <div class="em-svg-box em-svg-box--window">
      <SchemeFromState {...props} />
    </div>
  )
}

/**
 * Режим карты, когда движок не загрузился: схема на весь экран вместо пустоты,
 * с честной пометкой, что подложки нет.
 */
export function EmergencyScreenScheme(props: Omit<SchemeProps, 'venue' | 'start'>) {
  if (!engineFailed.value) return null
  return (
    <div class="em-svg-box em-svg-box--screen">
      <SchemeFromState {...props} />
      <p class="em-svg-note">{tr('emergency.no_engine')}</p>
    </div>
  )
}
